import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { unlockCandidateContact } from '../../api/talent';
import {
  displayRating,
  formatAvailabilityDate,
  formatSalary,
  getAvatarGradient,
  getInitials,
} from '../utils/profileDisplay';
import styles from './CandidateProfileModal.module.css';

const drawerTransition = { type: 'spring', damping: 30, stiffness: 300 };

function RatingBars({ rating, interviewCount }) {
  const normalized = displayRating(rating) ?? 0;
  const bars = useMemo(() => {
    const count = Math.min(5, Math.max(1, interviewCount || 1));
    return Array.from({ length: 5 }, (_, i) => {
      if (i >= count) return 0.15;
      const variance = 0.75 + ((i * 17) % 25) / 100;
      return Math.min(1, normalized / 10 * variance);
    });
  }, [normalized, interviewCount]);

  if (!interviewCount || interviewCount < 2) {
    const pct = Math.min(100, Math.max(0, (normalized / 10) * 100));
    return (
      <div className={styles.progressTrack}>
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
    );
  }

  return (
    <div className={styles.barChart} aria-hidden="true">
      {bars.map((h, i) => (
        <motion.div
          key={i}
          className={styles.bar}
          initial={{ height: 0 }}
          animate={{ height: `${Math.round(h * 100)}%` }}
          transition={{ delay: 0.15 + i * 0.06, duration: 0.4 }}
        />
      ))}
    </div>
  );
}

export default function CandidateProfileModal({ profile, onClose }) {
  const navigate = useNavigate();
  const [unlockedEmail, setUnlockedEmail] = useState(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const profileId = profile?.id;

  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!profile) return undefined;
    document.addEventListener('keydown', handleEscape);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = prev;
    };
  }, [profile, handleEscape]);

  useEffect(() => {
    setUnlockedEmail(null);
    setUnlockError(null);
    setSaved(false);
    setCopied(false);
  }, [profileId]);

  const handleUnlock = async () => {
    if (!profile?.id) return;
    setUnlocking(true);
    setUnlockError(null);
    try {
      const data = await unlockCandidateContact(profile.id);
      const email = data?.candidateEmail || data?.email;
      if (email) {
        setUnlockedEmail(email);
      } else {
        setUnlockError('Contact unlocked but no email was returned.');
      }
    } catch (err) {
      if (err?.status === 401) {
        navigate('/recruiter/login', { replace: true });
        return;
      }
      setUnlockError(err?.message || 'Could not unlock contact details.');
    } finally {
      setUnlocking(false);
    }
  };

  const handleCopy = async () => {
    if (!unlockedEmail) return;
    try {
      await navigator.clipboard.writeText(unlockedEmail);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setUnlockError('Copy failed — select the email manually.');
    }
  };

  const ratingNum = displayRating(profile?.samastixRating);
  const roleLocation = profile
    ? [profile.currentPosition, profile.location?.trim() || 'Remote'].filter(Boolean).join(' @ ')
    : '';

  const content = (
    <AnimatePresence>
      {profile && (
        <>
      <motion.div
        key="talent-overlay"
        className={styles.overlay}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <motion.aside
        key="talent-drawer"
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="candidate-profile-title"
        initial={{ x: '100%', opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: '100%', opacity: 0 }}
        transition={drawerTransition}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close profile"
        >
          ×
        </button>

        <div className={styles.scroll}>
          <motion.section
            className={styles.hero}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
          >
            <motion.div
              className={styles.avatarLg}
              style={{ background: getAvatarGradient(profile.candidateName) }}
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 320, damping: 22 }}
            >
              {getInitials(profile.candidateName)}
            </motion.div>
            <h2 id="candidate-profile-title" className={styles.heroName}>
              {profile.candidateName}
            </h2>
            {profile.headline && <p className={styles.heroHeadline}>{profile.headline}</p>}
            {roleLocation && <p className={styles.heroMeta}>{roleLocation}</p>}
          </motion.section>

          <motion.section
            className={styles.ratingSpotlight}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
          >
            <p className={styles.ratingBig}>
              ★ {ratingNum != null ? ratingNum.toFixed(1) : '—'}
            </p>
            <p className={styles.ratingLabel}>Samastix Verified Score</p>
            <p className={styles.ratingSub}>
              {profile.interviewCount ?? 0} AI interview
              {(profile.interviewCount ?? 0) === 1 ? '' : 's'} completed
            </p>
            <RatingBars
              rating={profile.samastixRating}
              interviewCount={profile.interviewCount}
            />
          </motion.section>

          {(profile.skills?.length ?? 0) > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.18 }}
            >
              <h3 className={styles.sectionTitle}>Skills & Expertise</h3>
              <div className={styles.skills}>
                {profile.skills.map((skill) => (
                  <span key={skill} className={styles.chip}>
                    {skill}
                  </span>
                ))}
              </div>
            </motion.section>
          )}

          <motion.section
            className={styles.detailsGrid}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
          >
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Experience</span>
              <span className={styles.detailValue}>
                {profile.yearsExperience != null ? `${profile.yearsExperience} years` : '—'}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Location</span>
              <span className={styles.detailValue}>
                {profile.location?.trim() || 'Remote'}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Availability</span>
              <span className={styles.detailValue}>
                {formatAvailabilityDate(profile.availableFrom)}
              </span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Expected Salary</span>
              <span className={styles.detailValue}>
                {formatSalary(profile.expectedSalary, 'Negotiable')}
              </span>
            </div>
          </motion.section>
        </div>

        <div className={styles.actions}>
          {unlockedEmail && (
            <div className={styles.unlockedBox}>
              <span className={styles.unlockedEmail}>{unlockedEmail}</span>
              <button type="button" className={styles.copyBtn} onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
          {unlockError && <p className={styles.errorText}>{unlockError}</p>}

          <motion.button
            type="button"
            className={styles.primaryBtn}
            disabled={unlocking || Boolean(unlockedEmail)}
            onClick={handleUnlock}
            whileHover={{
              boxShadow: '0 0 48px rgba(99, 102, 241, 0.75)',
              scale: 1.01,
            }}
            whileTap={{ scale: 0.98 }}
          >
            {unlocking
              ? 'Unlocking…'
              : unlockedEmail
                ? 'Contact unlocked'
                : 'Unlock Contact Details'}
          </motion.button>

          <motion.button
            type="button"
            className={`${styles.secondaryBtn} ${saved ? styles.secondaryBtnSaved : ''}`}
            onClick={() => setSaved((v) => !v)}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
          >
            {saved ? 'Saved to list ✓' : 'Save to List'}
          </motion.button>
        </div>
      </motion.aside>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
